import { Textarea as MantineTextArea, TextareaProps as MantineTextareaProps, Stack } from '@mantine/core';
import { forwardRef, useEffect, useId, useRef, useState } from 'react';

import inputStyles from '../styles/Input.module.css';
import { TextareaLabel } from './TextareaLabel';
import { joinClassNames } from 'utils/joinClassNames';

export interface TextareaProps extends Omit<MantineTextareaProps, 'radius'> {
    showCharacterCount?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
    function Textarea(props, forwardedRef) {
        const ref = useRef<HTMLTextAreaElement>(null);
        const id = useId();
        const textareaId = props.id || id;
        const [characterCount, setCharacterCount] = useState(
            props.value?.toString().length ?? props.defaultValue?.toString().length ?? 0
        );

        useEffect(() => {
            if (typeof props.onChange === 'function') {
                return;
            }

            function onInput(e: Event) {
                setCharacterCount((e.target as HTMLTextAreaElement)?.value?.length || 0);
            }

            if (ref && typeof ref !== 'function' && ref.current) {
                ref.current.addEventListener('input', onInput);

                return () => {
                    ref.current?.removeEventListener('input', onInput);
                };
            }
        }, [ref, props.onChange]);

        useEffect(() => {
            setCharacterCount(props.value?.toString().length ?? props.defaultValue?.toString().length ?? 0);
        }, [props.value, props.defaultValue]);

        return (
            <Stack gap={0}>
                <TextareaLabel
                    className={joinClassNames(
                        inputStyles.label,
                        props.classNames?.['label' as keyof typeof props.classNames],
                        props.labelProps?.className
                    )}
                    {...props.labelProps}
                    htmlFor={textareaId}
                    characterCount={characterCount}
                    withAsterisk={props.withAsterisk}
                    maxLength={props.maxLength}
                    showCharacterCount={props.showCharacterCount}
                >
                    {props.label}
                </TextareaLabel>
                <MantineTextArea
                    classNames={{
                        input: inputStyles.input,
                        description: inputStyles.description,
                        required: inputStyles.required,
                    }}
                    {...props}
                    id={textareaId}
                    ref={node => {
                        ref.current = node;

                        if (typeof forwardedRef === 'function') {
                            forwardedRef(node);
                        } else if (forwardedRef) {
                            forwardedRef.current = node;
                        }
                    }}
                    // Explicitly set the label to an empty string to prevent the default label from being rendered
                    label=""
                />
            </Stack>
        );
    }
);
